import { useMemo, useState } from 'react';

import { AddCircle, CheckCircleOutline, Clear, FormatListBulleted, Search } from '@mui/icons-material';
import {
	Box,
	Button,
	IconButton,
	InputAdornment,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';

import {
	AddPropertyV2Model,
	EnumModel,
	EnumPropertyModel,
	NewEntityAsNewPropertyRequest,
	PrimitiveType,
	SchemaPropertyModel,
	SimplePropertyModel,
} from '../../../api/api';
import { sortByName } from '../../../common/sortByName';
import AddPropertyRow from './AddPropertyRow';
import PropertyRow from './PropertyRow';
import SchemaPropertyRow from './SchemaPropertyRow';

interface Props {
	contextId: string;
	properties: SchemaPropertyModel[];
	onAddProperty: (property: SimplePropertyModel) => Promise<void> | void;
	onAddSchemaProperty: (property: NewEntityAsNewPropertyRequest) => Promise<void> | void;
	onAddReferenceProperty: (property: AddPropertyV2Model) => Promise<void> | void;
	onAddEnumProperty: (property: EnumPropertyModel) => Promise<void> | void;
	onDelete: (propertyName: string) => Promise<void> | void;
	onEditProperty: (property: SchemaPropertyModel) => Promise<void> | void;
	availableEnums: EnumModel[];
	propertyTypes: PrimitiveType[];
	fillAvailableHeight?: boolean;
}

const columnWidths = {
	name: '43%',
	type: '20%',
	required: '30px',
	multiple: '30px',
	default: '10%',
	remove: '15%',
};

type SortDirection = 'asc' | 'desc';
type SortField = 'name' | 'type' | 'required' | 'multiple' | 'default';

const getSortValue = (property: SchemaPropertyModel, sortField: SortField): string | number => {
	switch (sortField) {
		case 'name':
			return property.name?.toLocaleLowerCase() ?? '';
		case 'type':
			return (property.isReferenceProperty ? property.referenceSchemaShortName : property.type)?.toLocaleLowerCase() ?? '';
		case 'required':
			return property.isRequired ? 1 : 0;
		case 'multiple':
			return property.isMultiple ? 1 : 0;
		case 'default':
			return property.defaultValue?.toLocaleLowerCase() ?? '';
	}
};

const PropertyTable: React.FC<Props> = ({
	contextId,
	properties,
	availableEnums,
	propertyTypes,
	onAddProperty,
	onAddSchemaProperty,
	onAddReferenceProperty,
	onAddEnumProperty,
	onDelete,
	onEditProperty,
	fillAvailableHeight = false,
}) => {
	const [propertyNameFilter, setPropertyNameFilter] = useState('');
	const [isAddingProperty, setIsAddingProperty] = useState(false);
	const [sortField, setSortField] = useState<SortField>('name');
	const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
	const normalizedPropertyNameFilter = propertyNameFilter.trim().toLocaleLowerCase();
	const filteredProperties = useMemo(() => {
		const matchingProperties = normalizedPropertyNameFilter
			? (properties ?? []).filter(
					(property) =>
						property.name?.toLocaleLowerCase().includes(normalizedPropertyNameFilter) ||
						property.description?.toLocaleLowerCase().includes(normalizedPropertyNameFilter),
				)
			: [...(properties ?? [])];

		return matchingProperties.sort((left, right) => {
			const leftValue = getSortValue(left, sortField);
			const rightValue = getSortValue(right, sortField);
			const comparison =
				typeof leftValue === 'number' && typeof rightValue === 'number' ? leftValue - rightValue : String(leftValue).localeCompare(String(rightValue));

			if (comparison !== 0) {
				return sortDirection === 'asc' ? comparison : -comparison;
			}

			return sortByName(left, right);
		});
	}, [normalizedPropertyNameFilter, properties, sortDirection, sortField]);

	const handleSort = (nextSortField: SortField) => {
		if (sortField === nextSortField) {
			setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
			return;
		}

		setSortField(nextSortField);
		setSortDirection('asc');
	};

	const renderSortLabel = (field: SortField, label: JSX.Element | string) => (
		<TableSortLabel
			active={sortField === field}
			direction={sortField === field ? sortDirection : 'asc'}
			onClick={() => handleSort(field)}
			sx={{ '& .MuiTableSortLabel-icon': { mx: 0 } }}
		>
			{typeof label === 'string' ? <strong>{label}</strong> : label}
		</TableSortLabel>
	);

	return properties != null ? (
		<TableContainer
			component={Paper}
			sx={{
				mt: 1,
				width: '100%',
				...(fillAvailableHeight && {
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
					minHeight: 0,
					overflow: 'hidden',
				}),
			}}
		>
			<Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
				<TextField
					value={propertyNameFilter}
					onChange={(event) => setPropertyNameFilter(event.target.value)}
					placeholder="Filter properties"
					size="small"
					sx={{ flex: 1 }}
					inputProps={{ 'aria-label': 'Filter properties by name or description' }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search fontSize="small" />
							</InputAdornment>
						),
						endAdornment: propertyNameFilter ? (
							<InputAdornment position="end">
								<IconButton size="small" onClick={() => setPropertyNameFilter('')} aria-label="Clear property filter">
									<Clear fontSize="small" />
								</IconButton>
							</InputAdornment>
						) : undefined,
					}}
				/>
				<Button startIcon={<AddCircle />} variant="outlined" size="small" onClick={() => setIsAddingProperty(true)} disabled={isAddingProperty}>
					Add Property
				</Button>
			</Box>
			<Box sx={{ ...(fillAvailableHeight && { flex: 1, minHeight: 0, overflow: 'auto' }) }}>
				<Table stickyHeader={fillAvailableHeight} size="small" sx={{ tableLayout: 'fixed', minWidth: 800 }}>
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: columnWidths.name }}>
								{renderSortLabel('name', 'Name')}
							</TableCell>
							<TableCell sx={{ width: columnWidths.type }}>
								{renderSortLabel('type', 'Type')}
							</TableCell>
							<TableCell sx={{ width: columnWidths.required, px: 0.5, textAlign: 'center' }}>
								{renderSortLabel(
									'required',
									<Tooltip title="Required">
										<CheckCircleOutline fontSize="small" />
									</Tooltip>,
								)}
							</TableCell>
							<TableCell sx={{ width: columnWidths.multiple, px: 0.5, textAlign: 'center' }}>
								{renderSortLabel(
									'multiple',
									<Tooltip title="Multiple">
										<FormatListBulleted fontSize="small" />
									</Tooltip>,
								)}
							</TableCell>
							<TableCell sx={{ width: columnWidths.default }}>
								{renderSortLabel('default', 'Default')}
							</TableCell>
							<TableCell sx={{ width: columnWidths.remove }}>
								<strong></strong>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<AddPropertyRow
							onSave={onAddProperty}
							contextId={contextId}
							onSaveSchemaProperty={onAddSchemaProperty}
							onSaveReferenceProperty={onAddReferenceProperty}
							onSaveEnum={onAddEnumProperty}
							availableEnums={availableEnums}
							propertyTypes={propertyTypes}
							isEditing={isAddingProperty}
							onIsEditingChanged={setIsAddingProperty}
						/>
						{filteredProperties.map((prop, idx) =>
							prop.isReferenceProperty ? (
								<SchemaPropertyRow
									onDelete={onDelete}
									key={prop.name ?? idx}
									prop={prop}
									onEditProperty={onEditProperty}
								/>
							) : (
								<PropertyRow
									onDelete={onDelete}
									onEditProperty={onEditProperty}
									key={prop.name ?? idx}
									prop={prop}
									enumTypeName={availableEnums.find((enumModel) => enumModel.id === prop.enumId && enumModel.contextId === prop.enumContextId)?.name}
								/>
							),
						)}
						{filteredProperties.length === 0 && normalizedPropertyNameFilter && (
							<TableRow>
								<TableCell colSpan={6}>
									<Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
										No properties match the current filter.
									</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</Box>
		</TableContainer>
	) : (
		<Typography variant="body2" sx={{ pl: 2, mt: 1 }}>
			No properties defined.
		</Typography>
	);
};

export default PropertyTable;
